import { useFlash } from './useFlash'

/**
 * Put a value on the clipboard, and say so briefly.
 *
 * One behavior, one hook — the app copies things from several shapes of
 * control (a value you click, a token chip, a link beside a share) and they
 * differ in their chrome, not in what copying MEANS. Written per control it
 * comes out subtly different each time: some confirm, some do not, and one
 * holds "Copied" for ever because its state has nothing to reset it.
 *
 * The clipboard is optional because a page served over plain http from anything
 * but localhost has no clipboard API at all, and a control that threw there
 * would take the screen down with it.
 */
export function useCopy(): [copied: boolean, copy: (value: string) => void] {
  const [flashed, flash] = useFlash<true>()

  /**
   * It confirms only what actually happened.
   *
   * The write can fail with the API present — permission refused, the document
   * not focused — and it is a PROMISE, so a rejection left unawaited is both an
   * unhandled rejection and a tick saying the value is on the clipboard when it
   * is not. Somebody who trusts that tick pastes the last thing they copied.
   */
  const copy = (value: string) => {
    navigator.clipboard
      ?.writeText(value)
      .then(() => flash(true))
      .catch(() => {})
  }

  return [flashed === true, copy]
}
